import moment from 'moment';
import _ from 'lodash';
import { RepeatType, SessionTime } from '../types';
import { RecurringType } from '@scale/shared/webinarSessions/types';
import { TimeOptions } from 'react-scale/components/WebinarSessions/components/TimePicker';

const parseTimeString = (time: string): Date => {
  const [timePart, modifier] = time.split(/(AM|PM)/i);
  let [hours, minutes] = timePart.split(':').map(Number);
  if (modifier.toUpperCase() === 'PM' && hours !== 12) {
    hours += 12;
  } else if (modifier.toUpperCase() === 'AM' && hours === 12) {
    hours = 0;
  }
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

export const parseStartTimeFromDate = (date: Date) => {
  const formatted = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  // Remove the space between the hours and minutes
  return formatted.replace(/\s/g, '');
};

export const timeDifferenceInMinutes = (time1: string, time2: string) =>
  (parseTimeString(time1).valueOf() - parseTimeString(time2).valueOf()) / (1000 * 60);

export const addDurationToTimeString = (timeStr: string, duration: number): string => {
  const modifier = timeStr.slice(-2);
  const time = timeStr.slice(0, -2);
  let [hours, minutes] = time.split(':').map(Number);
  if (modifier === 'PM' && hours !== 12) hours += 12;
  else if (modifier === 'AM' && hours === 12) hours = 0;
  const date = new Date(0, 0, 0, hours, minutes + duration);
  let resultHours = date.getHours();
  const resultMinutes = date.getMinutes();
  const resultModifier = resultHours >= 12 ? 'PM' : 'AM';
  resultHours = resultHours % 12;
  if (resultHours === 0) resultHours = 12;
  const resultMinutesStr = resultMinutes < 10 ? '0' + resultMinutes : resultMinutes;
  const resultHoursStr = resultHours < 10 ? '0' + resultHours : resultHours;
  return `${resultHoursStr}:${resultMinutesStr}${resultModifier}`;
};

const addDays = (date: Date, days: number) => moment(new Date(date)).add(days, 'days').toDate();
const addWeeks = (date: Date, weeks: number) => moment(new Date(date)).add(weeks, 'weeks').toDate();
const addMonths = (date: Date, months: number) =>
  moment(new Date(date)).add(months, 'months').toDate();

const findNextDayOfTheWeek = (date: Date, day: string) => {
  const weekDayToFind = moment().day(day).weekday();
  const searchDate = moment(date).add(1, 'days');
  while (searchDate.weekday() !== weekDayToFind) searchDate.add(1, 'days');
  return searchDate.toDate();
};

interface Props {
  eventStart: Date | null;
  recurring: RecurringType;
  numOccurrences: number | null;
  repeatNumber: number | null;
  repeatType: RepeatType;
  daysOfTheWeek: string[];
  selectedDate: Date;
  additionalTimesOfDay: string[];
}

export const getNewStartDateTime = (d: Date, t: string) => {
  const date = new Date(d);
  date.setHours(parseTimeString(t).getHours(), parseTimeString(t).getMinutes(), 0, 0);
  return date;
};

export function calculateFutureOccurrencesV2({
  eventStart,
  startTime,
  endTime,
  hostedBy,
  recurring,
  numOccurrences,
  repeatNumber,
  repeatType,
  daysOfTheWeek,
  additionalTimesOfDay,
}: {
  eventStart: Date | null;
  startTime: string;
  endTime: string;
  hostedBy: string[];
  recurring: RecurringType;
  numOccurrences: number | null;
  repeatNumber: number | null;
  repeatType: RepeatType;
  daysOfTheWeek: string[];
  additionalTimesOfDay: SessionTime[];
}): SessionTime[] {
  let o: SessionTime[] = [];
  if (
    !eventStart ||
    !(recurring === RecurringType.RECURRING) ||
    !numOccurrences ||
    numOccurrences <= 1 ||
    !repeatNumber
  ) {
    o = [];
  } else if (repeatType === RepeatType.DAYS) {
    o = _.range(1, numOccurrences).map((n: number) => {
      const date = addDays(eventStart, n * repeatNumber);
      return {
        date,
        startTime,
        endTime,
        hostedBy,
      };
    });
    for (const sessionTime of additionalTimesOfDay) {
      o = [
        ...o,
        ..._.range(0, numOccurrences - 1).map((n: number) => ({
          date: addDays(sessionTime.date, n * repeatNumber),
          startTime: sessionTime.startTime,
          endTime: sessionTime.endTime,
          hostedBy: sessionTime.hostedBy,
        })),
      ];
    }
    o = o
      .sort(
        (a, b) =>
          getNewStartDateTime(a.date, a.startTime).getTime() -
          getNewStartDateTime(b.date, b.startTime).getTime(),
      )
      .filter(d => moment(d.date).isAfter())
      .slice(0, numOccurrences - 1);
  } else if (repeatType === RepeatType.WEEKS) {
    if (!daysOfTheWeek.length) {
      o = _.range(1, numOccurrences).map((n: number) => {
        const date = addWeeks(eventStart, n * repeatNumber);
        return {
          date,
          startTime,
          endTime,
          hostedBy,
        };
      });
      for (const sessionTime of additionalTimesOfDay) {
        o = [
          ...o,
          ..._.range(0, numOccurrences - 1).map((n: number) => ({
            date: addWeeks(sessionTime.date, n * repeatNumber),
            startTime: sessionTime.startTime,
            endTime: sessionTime.endTime,
            hostedBy: sessionTime.hostedBy,
          })),
        ];
      }
      o = o
        .sort(
          (a, b) =>
            getNewStartDateTime(a.date, a.startTime).getTime() -
            getNewStartDateTime(b.date, b.startTime).getTime(),
        )
        .filter(d => moment(d.date).isAfter())
        .slice(0, numOccurrences - 1);
    } else {
      const nextDaysOfTheWeek = daysOfTheWeek
        .map(day => {
          const date = findNextDayOfTheWeek(eventStart, day);
          return {
            date,
            startTime,
            endTime,
            hostedBy,
          };
        })
        .sort((a, b) => a.date.getTime() - b.date.getTime());
      const potentialOs = _.range(1, numOccurrences * daysOfTheWeek.length).map((n: number) => {
        const date = addWeeks(
          nextDaysOfTheWeek[(n - 1) % daysOfTheWeek.length].date,
          Math.ceil(n / daysOfTheWeek.length) * repeatNumber,
        );
        return {
          date,
          startTime,
          endTime,
          hostedBy,
        };
      });
      let potentialAdditionalOs: {
        date: Date;
        startTime: string;
        endTime: string;
        hostedBy: string[];
      }[] = [];
      for (const sessionTime of additionalTimesOfDay) {
        const nDOTW = daysOfTheWeek
          .map(day => findNextDayOfTheWeek(sessionTime.date, day))
          .sort((a, b) => a.getTime() - b.getTime());
        potentialAdditionalOs = [
          ...potentialAdditionalOs,
          ..._.range(0, (numOccurrences - 1) * daysOfTheWeek.length).map((n: number) => {
            const date = addWeeks(
              n === 0 ? sessionTime.date : nDOTW[(n - 1) % daysOfTheWeek.length],
              n === 0 ? 0 : Math.floor((n - 1) / daysOfTheWeek.length) * repeatNumber,
            );
            return {
              date,
              startTime: sessionTime.startTime,
              endTime: sessionTime.endTime,
              hostedBy: sessionTime.hostedBy,
            };
          }),
        ];
      }
      o = [...nextDaysOfTheWeek, ...potentialOs, ...potentialAdditionalOs];
      o = o
        .sort(
          (a, b) =>
            getNewStartDateTime(a.date, a.startTime).getTime() -
            getNewStartDateTime(b.date, b.startTime).getTime(),
        )
        .filter(d => moment(d.date).isAfter())
        .slice(0, numOccurrences - 1);
    }
  } else if (repeatType === RepeatType.MONTHS) {
    o = _.range(1, numOccurrences).map((n: number) => {
      const date = addMonths(eventStart, n * repeatNumber);
      return {
        date,
        startTime,
        endTime,
        hostedBy,
      };
    });
    for (const sessionTime of additionalTimesOfDay) {
      o = [
        ...o,
        ..._.range(0, numOccurrences - 1).map((n: number) => ({
          date: addMonths(sessionTime.date, n * repeatNumber),
          startTime: sessionTime.startTime,
          endTime: sessionTime.endTime,
          hostedBy: sessionTime.hostedBy,
        })),
      ];
    }
    o = o
      .sort(
        (a, b) =>
          getNewStartDateTime(a.date, a.startTime).getTime() -
          getNewStartDateTime(b.date, b.startTime).getTime(),
      )
      .filter(d => moment(d.date).isAfter())
      .slice(0, numOccurrences - 1);
  }
  return o;
}

export const calculateFutureOccurrences: (props: Props) => Date[] = props => {
  const {
    eventStart,
    recurring,
    numOccurrences,
    repeatNumber,
    repeatType,
    daysOfTheWeek,
    selectedDate,
    additionalTimesOfDay,
  } = props;
  let o: Date[] = [];
  if (
    !eventStart ||
    !(recurring === RecurringType.RECURRING) ||
    !numOccurrences ||
    numOccurrences <= 1 ||
    !repeatNumber
  )
    o = [];
  else if (repeatType === RepeatType.DAYS) {
    o = _.range(1, numOccurrences).map((n: number) => addDays(eventStart, n * repeatNumber));
    for (const startT of additionalTimesOfDay) {
      const startDate = getNewStartDateTime(selectedDate, startT);
      o = [
        ...o,
        ..._.range(0, numOccurrences - 1).map((n: number) => addDays(startDate, n * repeatNumber)),
      ];
    }
    o = o
      .sort((a, b) => a.getTime() - b.getTime())
      .filter(d => moment(d).isAfter())
      .slice(0, numOccurrences - 1);
  } else if (repeatType === RepeatType.WEEKS) {
    if (!daysOfTheWeek.length) {
      o = _.range(1, numOccurrences).map((n: number) => addWeeks(eventStart, n * repeatNumber));
      for (const startT of additionalTimesOfDay) {
        const startDate = getNewStartDateTime(selectedDate, startT);
        o = [
          ...o,
          ..._.range(0, numOccurrences - 1).map((n: number) =>
            addWeeks(startDate, n * repeatNumber),
          ),
        ];
      }
      o = o
        .sort((a, b) => a.getTime() - b.getTime())
        .filter(d => moment(d).isAfter())
        .slice(0, numOccurrences - 1);
    } else {
      const nextDaysOfTheWeek = daysOfTheWeek
        .map(day => findNextDayOfTheWeek(eventStart, day))
        .sort((a, b) => a.getTime() - b.getTime());
      const potentialOs = _.range(1, numOccurrences * daysOfTheWeek.length).map((n: number) =>
        addWeeks(
          nextDaysOfTheWeek[(n - 1) % daysOfTheWeek.length],
          Math.ceil(n / daysOfTheWeek.length) * repeatNumber,
        ),
      );
      let potentialAdditionalOs: Date[] = [];
      for (const startT of additionalTimesOfDay) {
        const startDate = getNewStartDateTime(selectedDate, startT);
        const nDOTW = daysOfTheWeek
          .map(day => findNextDayOfTheWeek(startDate, day))
          .sort((a, b) => a.getTime() - b.getTime());
        potentialAdditionalOs = [
          ...potentialAdditionalOs,
          ..._.range(0, (numOccurrences - 1) * daysOfTheWeek.length).map((n: number) =>
            addWeeks(
              n === 0 ? startDate : nDOTW[(n - 1) % daysOfTheWeek.length],
              n === 0 ? 0 : Math.floor((n - 1) / daysOfTheWeek.length) * repeatNumber,
            ),
          ),
        ];
      }
      o = [...nextDaysOfTheWeek, ...potentialOs, ...potentialAdditionalOs];
      o = [...new Set(o.map(d => d.toString()))]
        .map(d => new Date(d))
        .sort((a, b) => a.getTime() - b.getTime())
        .filter(d => moment(d).isAfter())
        .slice(0, numOccurrences - 1);
    }
  } else if (repeatType === RepeatType.MONTHS) {
    o = _.range(1, numOccurrences).map((n: number) => addMonths(eventStart, n * repeatNumber));
    for (const startT of additionalTimesOfDay) {
      const startDate = getNewStartDateTime(selectedDate, startT);
      o = [
        ...o,
        ..._.range(0, numOccurrences - 1).map((n: number) =>
          addMonths(startDate, n * repeatNumber),
        ),
      ];
    }
    o = o
      .sort((a, b) => a.getTime() - b.getTime())
      .filter(d => moment(d).isAfter())
      .slice(0, numOccurrences - 1);
  }
  return o;
};

export const getStartTimeFromDate = (date: Date) => {
  const currentTime = new Date();

  // If the date is today, set the start time to the next available time
  if (date.toDateString() === currentTime.toDateString()) {
    const timeOptionsInDateFormat = TimeOptions.map(option => parseTimeString(option.id));
    const nextTimeIndex = timeOptionsInDateFormat.findIndex(option => option > currentTime);
    if (nextTimeIndex !== -1) {
      return TimeOptions[nextTimeIndex].id;
    }
    return TimeOptions[TimeOptions.length - 1].id;
  }

  return '08:00AM';
};

export const getEndTimeFromStartTime = (startTime: string) => {
  const currentTimeIndex = TimeOptions.findIndex(option => option.id === startTime);

  // Try to set the end time to 30 minutes after the start time, otherwise set it to the next available time
  if (currentTimeIndex + 2 < TimeOptions.length) {
    return TimeOptions[currentTimeIndex + 2].id;
  } else if (currentTimeIndex + 1 < TimeOptions.length) {
    return TimeOptions[currentTimeIndex + 1].id;
  }
  return TimeOptions[currentTimeIndex].id;
};
